import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import Utils from 'app/common/util/Utils';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';
import { CountType, FlightDto, FlightId, LoadClassification, TimePeriod } from 'app/model/CORE';
import { ExtendedMonitoredTv, Sol49FlightAirspaceCrossing, Sol49FlightDto } from 'app/model/SOL49';
import { PathPointLoadClassification } from 'app/unity/ContentViewerTypes/PathPointLoadClassification';

// NOTE(bjarne): These functions are all copied from NetworkMonitoringMap.
export class DashboardFlightUtils {
    public computeMapFlightDataWithTvLoads(flights: FlightDto[], display: CountType) {
        const highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        for (const flight of flights) {
            if (flight['currentProfileAirspacePoints']) {
                const flightId = flight.flightId;
                const aspCrossings: Array<Sol49FlightAirspaceCrossing> = (flight as Sol49FlightDto)
                    .currentProfileAirspacePoints;
                if (aspCrossings.length > 0) {
                    for (const aspCrossing of aspCrossings.filter(
                        sol49AspCrossing => sol49AspCrossing.countType === display,
                    )) {
                        let loadClassification: PathPointLoadClassification;
                        // TODO(bjarne): Store loadValue.nonRelevant in the segment as well?
                        switch (aspCrossing.loadValue.loadClassification) {
                            case LoadClassification.OVERLOAD:
                                loadClassification = PathPointLoadClassification.OVERLOAD;
                                break;
                            case LoadClassification.HIGH_THRESHOLD:
                                loadClassification = PathPointLoadClassification.HIGH;
                                break;
                            case LoadClassification.LOW_THRESHOLD:
                                loadClassification = PathPointLoadClassification.LOW;
                                break;
                        }
                        if (loadClassification) {
                            const aspCrossingTimePeriod = {
                                from: aspCrossing.firstEntryTime,
                                to: aspCrossing.lastExitTime,
                            };
                            const aspCrossingLoadIntersectingTimePeriod = TimePeriodUtils.intersection(
                                aspCrossingTimePeriod,
                                aspCrossing.loadValue.timePeriod,
                            );
                            let segments: ProfileSegmentHighlight[] = highlightedSegments.get(flightId);
                            if (!segments) {
                                segments = [];
                                highlightedSegments.set(flightId, segments);
                            }
                            segments.push({
                                loadClassification: loadClassification,
                                // timePeriod: {from: aspCrossing.firstEntryTime, to: aspCrossing.lastExitTime}
                                timePeriod: aspCrossingLoadIntersectingTimePeriod,
                            });
                        }
                    }
                }
            }
        }
        // Remove duplicate segments - happens when a TV that is not monitorable is regulated (segments overlap)
        const deconflictedHighlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        highlightedSegments.forEach((value, key) => {
            const valueAfter = this.deconflictHighlightedSegments(value);
            deconflictedHighlightedSegments.set(key, valueAfter);
        });
        return deconflictedHighlightedSegments;
    }

    public getHighlightedSegments(
        tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> },
        monitoredTvs?: ExtendedMonitoredTv[],
    ): Map<FlightId, ProfileSegmentHighlight[]> {
        const highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        if (tvsOccupancyPeriods && monitoredTvs) {
            for (const tvId in tvsOccupancyPeriods) {
                const monitoredTv = monitoredTvs.find(monitoredTv => tvId === monitoredTv.refLocation.id);

                if (monitoredTv) {
                    // Could be null if the TV is outside of the area of responsibility
                    tvsOccupancyPeriods[tvId].forEach((occupancyPeriod: TimePeriod, flightId: FlightId) => {
                        for (const mlv of monitoredTv.loadValues) {
                            // Exclude MLVs that are outside of the activation period
                            if (
                                MonitoredLoadUtils.getActivePeriodForOccupancy(
                                    monitoredTv.activePeriods,
                                    mlv.timePeriod,
                                ) &&
                                TimePeriodUtils.intersects(mlv.timePeriod, occupancyPeriod)
                            ) {
                                // Parts of the trajectories that are outside of the monitored period are de-facto ignored
                                let loadClassification: PathPointLoadClassification;
                                // TODO(bjarne): Store loadValue.nonRelevant in the segment as well?
                                switch (mlv.loadClassification) {
                                    case LoadClassification.OVERLOAD:
                                        loadClassification = PathPointLoadClassification.OVERLOAD;
                                        break;
                                    case LoadClassification.HIGH_THRESHOLD:
                                        loadClassification = PathPointLoadClassification.HIGH;
                                        break;
                                    case LoadClassification.LOW_THRESHOLD:
                                        loadClassification = PathPointLoadClassification.LOW;
                                        break;
                                }
                                if (loadClassification) {
                                    let segments: ProfileSegmentHighlight[] = highlightedSegments.get(flightId);
                                    if (!segments) {
                                        segments = [];
                                        highlightedSegments.set(flightId, segments);
                                    }
                                    segments.push({
                                        loadClassification: loadClassification,
                                        timePeriod: TimePeriodUtils.intersection(mlv.timePeriod, occupancyPeriod),
                                    });
                                }
                            }
                        }
                    });
                }
            }
        }

        // Remove duplicate segments - happens when a TV that is not monitorable is regulated (segments overlap)
        const deconflictedHighlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        highlightedSegments.forEach((value, key) => {
            const valueAfter = this.deconflictHighlightedSegments(value);
            deconflictedHighlightedSegments.set(key, valueAfter);
        });
        return deconflictedHighlightedSegments;
    }

    public mergeMap<K, V>(map1: Map<K, Array<V>>, map2: Map<K, Array<V>>): Map<K, Array<V>> {
        let result = new Map<K, Array<V>>();
        if (map1) {
            result = new Map<K, Array<V>>(map1);
        }
        if (map2) {
            for (const key of Array.from(map2.keys())) {
                if (result.has(key)) {
                    result.set(key, [...result.get(key), ...map2.get(key)]);
                } else {
                    result.set(key, map2.get(key));
                }
            }
        }
        //TODO deconflict?
        return result;
    }

    // NOTE(bjarne): Copied from NetworkMonitoringMap.
    private deconflictHighlightedSegments(segments: ProfileSegmentHighlight[]): ProfileSegmentHighlight[] {
        const highlightedSegments = segments.slice();
        const splitTimes: Date[] = [];
        highlightedSegments.forEach(value => {
            Utils.addAllIfNeeded(splitTimes, [value.timePeriod.from, value.timePeriod.to]);
        });
        splitTimes.sort((d1: Date, d2: Date) => TimeUtils.compare(d1, d2));

        const deconflictedHighlightedSegments: ProfileSegmentHighlight[] = [];
        for (let i = 0; i < splitTimes.length - 1; i++) {
            const timePeriod = { from: splitTimes[i], to: splitTimes[i + 1] };

            for (let j = 0; j < highlightedSegments.length; j++) {
                if (TimePeriodUtils.contains(highlightedSegments[j].timePeriod, timePeriod)) {
                    // TODO: Loop over all segments, and keep the one with the worst colour
                    deconflictedHighlightedSegments.push({
                        loadClassification: highlightedSegments[j].loadClassification,
                        timePeriod: timePeriod,
                    });
                    break;
                }
            }
        }
        return deconflictedHighlightedSegments;
    }
}
